/**
 * Clinic seat pricing — D2 restructure (`_docs/decizii-abonare-founding-2026-09-19.md` §2.5).
 *
 * A clinic plan is priced as **one base subscription + a per-doctor rate**, the
 * rate coming from the bracket the covered-doctor count falls into:
 *
 *   Clinic Pro     €99  + €19 / €15 / €12 per doctor  (1–5 / 6–15 / 16+)
 *   Clinic Premium €199 + €29 / €25 / €19 per doctor  (1–5 / 6–15 / 16+)
 *
 *   monthly = basePriceMonthly + pricePerSeat(bracket(doctors)) × doctors
 *
 * The rate is flat for every seat, not marginal. Mirrors apps/web's copy at
 * apps/web/src/components/pricing/clinic-seat-pricing.ts and what
 * `GET /api/pricing/clinic-quote` answers (the two apps have no shared UI
 * package yet — keep them in step).
 *
 * `basePriceMonthly` / `perSeatTiers` are OPTIONAL on the wire: absent on every
 * non-facility plan, and absent everywhere until the API side of D2 ships. The
 * helpers below fall back to `priceMonthly` / `volumeTiers` so both shapes work.
 */

/**
 * Hard cap on covered doctors, mirroring `MAX_CLINIC_DOCTORS` in
 * `apps/api/src/lib/pricing.ts` — every stepper/slider/clamp in the SPA and on
 * the marketing site must stop where the server's validation does.
 */
export const MAX_CLINIC_DOCTORS = 500;

/** Cap for plans that are NOT seat-priced — mirrors `MAX_NON_SEAT_QUANTITY`. */
export const MAX_NON_SEAT_QUANTITY = 100;

export interface PerSeatTier {
  minSeats: number;
  maxSeats: number;
  pricePerSeat: number;
}

/** Shape of `GET /api/pricing/clinic-quote`. */
export interface ClinicQuote {
  planCode?: string;
  doctors: number;
  basePriceMonthly: number;
  perSeatPrice: number;
  /**
   * Seats actually charged. Differs from `doctors` only when the plan includes
   * some seats in the base price (`providersIncluded > 0`).
   */
  paidSeats?: number;
  seatsMonthly: number;
  monthly: number;
  /**
   * ALWAYS `null` from the API for clinic plans: they are monthly-only
   * (decision 2), so there is no annual price to quote. Never render it — the
   * yearly figure a clinic cares about is `monthly × 12`, which the calculators
   * compute themselves.
   */
  annual: number | null;
  annualAvailable?: boolean;
  currency?: string;
  tier?: PerSeatTier | null;
}

/** Structural subset of `PricingTier` these helpers read. */
export interface SeatPricedPlan {
  planCode?: string;
  priceMonthly: number;
  basePriceMonthly?: number | null;
  perSeatTiers?: PerSeatTier[] | null;
  volumeTiers?: { minProviders: number; maxProviders: number; pricePerProvider: number }[] | null;
}

/** Plan codes billed per covered doctor (seed: `facility_*`). */
export function isFacilityPlanCode(planCode: string | undefined): boolean {
  return !!planCode && (planCode.startsWith("facility_") || planCode.startsWith("clinic_"));
}

/**
 * Per-seat brackets for a clinic plan.
 *
 * Prefers the D2 field (`perSeatTiers`). For a **facility** plan it also accepts
 * the pre-D2 `volumeDiscountTiers` shape, so the UI keeps working against an API
 * that has not shipped the new field yet. Provider plans are deliberately
 * excluded: they carry `volumeTiers` too (the old "add doctors to my own plan"
 * discount) and are NOT priced base + per covered doctor.
 */
export function seatTiersOf(plan: SeatPricedPlan): PerSeatTier[] | null {
  if (plan.perSeatTiers && plan.perSeatTiers.length > 0) {
    return [...plan.perSeatTiers].sort((a, b) => a.minSeats - b.minSeats);
  }
  if (isFacilityPlanCode(plan.planCode) && plan.volumeTiers && plan.volumeTiers.length > 0) {
    return plan.volumeTiers
      .map((v) => ({
        minSeats: v.minProviders,
        maxSeats: v.maxProviders,
        pricePerSeat: v.pricePerProvider,
      }))
      .sort((a, b) => a.minSeats - b.minSeats);
  }
  return null;
}

/** Base subscription price — D2 field first, plan price as the fallback. */
export function baseOf(plan: SeatPricedPlan): number {
  return plan.basePriceMonthly ?? plan.priceMonthly;
}

/** True when the plan is billed as "base + per covered doctor". */
export function isSeatPriced(plan: SeatPricedPlan): boolean {
  return baseOf(plan) > 0 && (seatTiersOf(plan)?.length ?? 0) > 0;
}

/**
 * True only when the API answered the D2 field itself. Call sites that must not
 * contradict the *old* server-side charge math (checkout summary) gate on this
 * rather than on `isSeatPriced`, which also accepts the legacy fallback.
 */
export function hasPerSeatTiers(plan: SeatPricedPlan): boolean {
  return baseOf(plan) > 0 && (plan.perSeatTiers?.length ?? 0) > 0;
}

/** Client mirror of `maxQuantityFor(plan)` in apps/api. */
export function maxQuantityFor(plan: SeatPricedPlan): number {
  return hasPerSeatTiers(plan) ? MAX_CLINIC_DOCTORS : MAX_NON_SEAT_QUANTITY;
}

/** The bracket `doctors` falls into. Clamps to the outer brackets. */
export function resolveSeatTier(tiers: PerSeatTier[], doctors: number): PerSeatTier | null {
  if (tiers.length === 0) return null;
  const hit = tiers.find((t) => doctors >= t.minSeats && doctors <= t.maxSeats);
  if (hit) return hit;
  const first = tiers[0]!;
  const last = tiers[tiers.length - 1]!;
  return doctors < first.minSeats ? first : last;
}

/** `monthly = base + pricePerSeat(bracket) × doctors`. */
export function clinicQuote(plan: SeatPricedPlan, doctors: number): ClinicQuote {
  const base = baseOf(plan);
  const tiers = seatTiersOf(plan) ?? [];
  const seatTier = resolveSeatTier(tiers, doctors);
  const perSeatPrice = seatTier?.pricePerSeat ?? 0;
  const seatsMonthly = perSeatPrice * doctors;
  const monthly = base + seatsMonthly;

  return {
    planCode: plan.planCode,
    doctors,
    basePriceMonthly: base,
    perSeatPrice,
    seatsMonthly,
    monthly,
    // The API answers `annual: null` (clinics are monthly-only, decision 2).
    // Kept as the 12-month run rate locally so nothing downstream can divide by
    // or print a null; the UI computes `monthly × 12` itself either way.
    annual: monthly * 12,
    annualAvailable: false,
    paidSeats: doctors,
    tier: seatTier,
  };
}
